import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  LinkOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Loading from "components/shared-components/Loading";
import Flex from "components/shared-components/Flex";
import { usePickRoute, useProducts } from "../../../../data/hooks";
import { findOrphanedEntries, findUnplacedProducts, moveEntry, normalizeProductName } from "../../../../functions/pickRoute";

const SHOP_COLORS = { VCE: "green", HVO: "purple" };

/**
 * Pick route - the order in which the freezer is walked, and the single list both shops'
 * packing slips are sorted by.
 *
 * Reordering happens on a local working copy and is written in one go, so fine-tuning a
 * route of several hundred entries does not turn into several hundred Firestore writes.
 */
const PickRoute = () => {
  const { t } = useTranslation();
  const { products, isLoading: productsLoading } = useProducts();
  const {
    entries,
    isLoading: routeLoading,
    saveOrder,
    generateFromProducts,
    syncFromProducts,
    resetOrder,
    deleteEntry,
    attachProduct,
    detachProduct,
  } = usePickRoute();

  // Working copy: the route as it would be after the edits that are not saved yet
  const [draft, setDraft] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [productToLink, setProductToLink] = useState(null);

  const isLoading = routeLoading || productsLoading;

  // Adopt the stored route whenever it changes, unless there is unsaved reordering to protect
  useEffect(() => {
    if (isDirty) return;
    setDraft(entries);
  }, [entries, isDirty]);

  const unplacedProducts = useMemo(() => findUnplacedProducts(entries, products), [entries, products]);
  const orphanedEntries = useMemo(() => findOrphanedEntries(entries, products), [entries, products]);

  const filteredDraft = useMemo(() => {
    const needle = normalizeProductName(searchText);
    if (!needle) return draft;
    return draft.filter((entry) => {
      const haystack = [entry.label, ...(entry.products || []).map((product) => product.name)]
        .map(normalizeProductName)
        .join(" ");
      return haystack.includes(needle);
    });
  }, [draft, searchText]);

  // Reordering is relative to the whole route, so a filtered view cannot be dragged through.
  // Position numbers still work while searching - they address the full list directly.
  const isFiltered = filteredDraft.length !== draft.length;

  const applyMove = (entry, toIndex) => {
    const fromIndex = draft.findIndex((candidate) => candidate.id === entry.id);
    if (fromIndex === -1) return;
    setDraft(moveEntry(draft, fromIndex, toIndex));
    setIsDirty(true);
  };

  const runAction = async (action, successMessage) => {
    setIsBusy(true);
    try {
      const result = await action();
      if (successMessage) message.success(successMessage(result), 4);
    } catch (error) {
      if (error.message === "pickRoute/not-empty") {
        message.error(t("pick_route.error_not_empty"), 5);
      } else {
        message.error(error.message, 5);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveOrder = () =>
    runAction(
      async () => {
        await saveOrder(draft);
        setIsDirty(false);
      },
      () => t("pick_route.order_saved"),
    );

  const handleDiscard = () => {
    setDraft(entries);
    setIsDirty(false);
  };

  const handleGenerate = () =>
    runAction(
      () => generateFromProducts(products),
      (seeded) => t("pick_route.generated", { count: seeded.length }),
    );

  const handleSync = () =>
    runAction(
      () => syncFromProducts(products),
      (changes) =>
        t("pick_route.synced", {
          created: changes.created.length,
          attached: changes.attached.reduce((total, addition) => total + addition.products.length, 0),
        }),
    );

  const handleReset = () =>
    runAction(
      async () => {
        await resetOrder(products);
        setIsDirty(false);
      },
      () => t("pick_route.order_reset"),
    );

  const handleLinkProduct = async () => {
    const product = unplacedProducts.find(
      (candidate) => `${candidate.shopSource}|${candidate.id}` === productToLink,
    );
    if (!product || !linkTarget) return;
    await runAction(
      () => attachProduct(linkTarget, product),
      () => t("pick_route.product_linked", { product: product.name, entry: linkTarget.label }),
    );
    setLinkTarget(null);
    setProductToLink(null);
  };

  const columns = [
    {
      title: "#",
      dataIndex: "sequence",
      width: 110,
      render: (_, entry) => {
        const position = draft.findIndex((candidate) => candidate.id === entry.id);
        return (
          <InputNumber
            size="small"
            min={1}
            max={draft.length}
            value={position + 1}
            style={{ width: 80 }}
            // Committed on blur/enter rather than per keystroke, so typing "12" does not
            // first move the entry to position 1
            onPressEnter={(event) => applyMove(entry, Number(event.target.value) - 1)}
            onBlur={(event) => {
              const next = Number(event.target.value) - 1;
              if (!Number.isNaN(next) && next !== position) applyMove(entry, next);
            }}
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "move",
      width: 80,
      render: (_, entry) => {
        const position = draft.findIndex((candidate) => candidate.id === entry.id);
        return (
          <Space size={4}>
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={position <= 0 || isFiltered}
              onClick={() => applyMove(entry, position - 1)}
            />
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={position === -1 || position >= draft.length - 1 || isFiltered}
              onClick={() => applyMove(entry, position + 1)}
            />
          </Space>
        );
      },
    },
    {
      title: t("product"),
      dataIndex: "label",
      render: (label) => <strong>{label || "-"}</strong>,
    },
    {
      title: t("pick_route.linked_products"),
      dataIndex: "products",
      render: (entryProducts, entry) => {
        if (!entryProducts || entryProducts.length === 0) {
          return <Tag color="red">{t("pick_route.no_products")}</Tag>;
        }
        return (
          <Space size={4} wrap>
            {entryProducts.map((product) => (
              <Tooltip
                key={`${product.shopSource}|${product.productId}`}
                title={`${product.name || "-"} (${product.shopSource})`}
              >
                <Tag
                  color={SHOP_COLORS[product.shopSource] || "default"}
                  closable={entryProducts.length > 1}
                  closeIcon={<DisconnectOutlined />}
                  onClose={(event) => {
                    event.preventDefault();
                    detachProduct(entry, product);
                  }}
                >
                  {product.shopSource} · {product.name || product.productId}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      width: 100,
      render: (_, entry) => (
        <Space size={4}>
          <Tooltip title={t("pick_route.link_product")}>
            <Button
              size="small"
              icon={<LinkOutlined />}
              disabled={unplacedProducts.length === 0}
              onClick={() => setLinkTarget(entry)}
            />
          </Tooltip>
          <Popconfirm
            title={t("pick_route.confirm_delete", { name: entry.label })}
            okText={t("yes")}
            cancelText={t("no")}
            onConfirm={() => deleteEntry(entry.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) return <Loading cover="content" />;

  return (
    <Card
      title={t("pick_route.title")}
      extra={
        <Space>
          {isDirty && (
            <>
              <Button onClick={handleDiscard} disabled={isBusy}>
                {t("pick_route.discard")}
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={isBusy} onClick={handleSaveOrder}>
                {t("pick_route.save_order")}
              </Button>
            </>
          )}
          {!isDirty && entries.length > 0 && (
            <>
              <Button icon={<ReloadOutlined />} loading={isBusy} onClick={handleSync}>
                {t("pick_route.sync")}
              </Button>
              <Popconfirm
                title={t("pick_route.confirm_reset")}
                okText={t("yes")}
                cancelText={t("no")}
                onConfirm={handleReset}
              >
                <Button icon={<ThunderboltOutlined />} loading={isBusy}>
                  {t("pick_route.reset")}
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      }
    >
      <p style={{ maxWidth: 780, color: "#6b6b6b" }}>{t("pick_route.explanation")}</p>

      {entries.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.empty_title")}
          description={t("pick_route.empty_description")}
          action={
            <Button type="primary" loading={isBusy} onClick={handleGenerate}>
              {t("pick_route.generate")}
            </Button>
          }
        />
      )}

      {entries.length > 0 && unplacedProducts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.unplaced_title", { count: unplacedProducts.length })}
          description={t("pick_route.unplaced_description")}
          action={
            <Button loading={isBusy} onClick={handleSync}>
              {t("pick_route.sync")}
            </Button>
          }
        />
      )}

      {orphanedEntries.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.orphaned_title", { count: orphanedEntries.length })}
          description={t("pick_route.orphaned_description")}
        />
      )}

      {isDirty && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.unsaved_title")}
          description={t("pick_route.unsaved_description")}
        />
      )}

      <Flex justifyContent="between" alignItems="center" className="mb-3">
        <Input
          value={searchText}
          placeholder={t("search")}
          prefix={<SearchOutlined />}
          style={{ maxWidth: 320 }}
          onChange={(event) => setSearchText(event.currentTarget.value)}
        />
        <span style={{ color: "#6b6b6b" }}>{t("pick_route.entry_count", { count: draft.length })}</span>
      </Flex>

      {isFiltered && (
        <Alert type="info" showIcon className="mb-3" message={t("pick_route.filtered_hint")} />
      )}

      <div className="table-responsive">
        <Table
          showSorterTooltip={false}
          columns={columns}
          dataSource={filteredDraft}
          rowKey="id"
          size="small"
          pagination={{ defaultPageSize: 50, showSizeChanger: true }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("pick_route.no_entries")} /> }}
        />
      </div>

      <Modal
        open={!!linkTarget}
        title={t("pick_route.link_modal_title", { entry: linkTarget?.label })}
        okText={t("pick_route.link_product")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !productToLink, loading: isBusy }}
        onOk={handleLinkProduct}
        onCancel={() => {
          setLinkTarget(null);
          setProductToLink(null);
        }}
      >
        <p style={{ color: "#6b6b6b" }}>{t("pick_route.link_modal_description")}</p>
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder={t("pick_route.select_product")}
          value={productToLink}
          onChange={setProductToLink}
          filterOption={(input, option) => normalizeProductName(option.label).includes(normalizeProductName(input))}
          options={unplacedProducts.map((product) => ({
            value: `${product.shopSource}|${product.id}`,
            label: `${product.shopSource} · ${product.name}`,
          }))}
        />
      </Modal>
    </Card>
  );
};

export default PickRoute;
